import Delta from 'quill-delta';

interface QuillDelta {
  ops: Array<{
    insert?: string;
    attributes?: {
      bold?: boolean;
      italic?: boolean;
      underline?: boolean;
      color?: string;
      background?: string;
      link?: string;
      list?: 'ordered' | 'bullet';
      header?: number;
      align?: 'left' | 'center' | 'right' | 'justify';
      [key: string]: any;
    };
    delete?: number;
    retain?: number;
  }>;
}

/**
 * Converts a Quill Delta JSON to HTML rich text
 * @param quillJson - The Quill Delta JSON object
 * @returns HTML string with rich text formatting
 */
export function quillDeltaToHtml(quillJson: QuillDelta): string {
  if (!quillJson || !quillJson.ops) {
    return '';
  }

  // Create a new Delta instance from the JSON
  const delta = new Delta(quillJson.ops);
  let html = '';

  // Process each operation in the delta
  delta.ops.forEach((op: any) => {
    if (op.insert) {
      let text = op.insert;
      const attrs = op.attributes || {};

      // Handle text formatting
      if (attrs.bold) text = `<strong>${text}</strong>`;
      if (attrs.italic) text = `<em>${text}</em>`;
      if (attrs.underline) text = `<u>${text}</u>`;
      
      // Handle colors
      if (attrs.color) text = `<span style="color: ${attrs.color}">${text}</span>`;
      if (attrs.background) text = `<span style="background-color: ${attrs.background}">${text}</span>`;
      
      // Handle links
      if (attrs.link) text = `<a href="${attrs.link}">${text}</a>`;

      // Handle line-level formatting
      if (attrs.header) {
        text = `<h${attrs.header}>${text}</h${attrs.header}>`;
      } else if (attrs.list === 'ordered') {
        text = `<li>${text}</li>`;
      } else if (attrs.list === 'bullet') {
        text = `<li>${text}</li>`;
      } else if (attrs.align) {
        text = `<div style="text-align: ${attrs.align}">${text}</div>`;
      } else {
        text = `<p>${text}</p>`;
      }

      html += text;
    }
  });

  // Wrap lists in appropriate tags
  if (html.includes('<li>')) {
    const listType = quillJson.ops.find(op => op.attributes?.list)?.attributes?.list;
    const listTag = listType === 'ordered' ? 'ol' : 'ul';
    html = `<${listTag}>${html}</${listTag}>`;
  }

  return html;
}

/**
 * Converts a Quill Delta JSON to plain text
 * @param quillJson - The Quill Delta JSON object
 * @returns Plain text string
 */
export function quillDeltaToPlainText(quillJson: QuillDelta): string {
  if (!quillJson || !quillJson.ops) {
    return '';
  }

  // Create a new Delta instance from the JSON
  const delta = new Delta(quillJson.ops);
  let text = '';

  // Process each operation in the delta
  delta.ops.forEach((op: any) => {
    if (op.insert) {
      text += op.insert;
    }
  });

  return text;
} 